import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export type SiteChartVersion = 'legacy' | 'new';

export type SiteChartVersionTrigger = 'toggle' | 'spillage_table' | 'alert_view' | 'initial';

type HotjarApi = typeof import('@hotjar/browser').default;

@Injectable({ providedIn: 'root' })
export class HotjarTrackingService {
  private hotjar: HotjarApi | null = null;
  private initPromise: Promise<void> | null = null;

  private loadHotjar(): Promise<HotjarApi> {
    return import('@hotjar/browser').then(module => module.default);
  }

  private getSiteChartVersionEventName(
    version: SiteChartVersion,
    trigger: SiteChartVersionTrigger
  ): string {
    const direction = version === 'new' ? 'to_new' : 'to_legacy';

    if (trigger === 'toggle') {
      return `site_chart_switched_${direction}`;
    }

    return `site_chart_switched_${direction}_${trigger}`;
  }

  private isReady(): Promise<void> {
    if (!this.initPromise) {
      this.init();
    }
    if (!environment.hotjarEnabled) {
      return Promise.resolve();
    }

    return this.initPromise!;
  }

  public init(): void {
    if (!environment.hotjarEnabled || this.initPromise) {
      return;
    }

    this.initPromise = this.loadHotjar().then(Hotjar => {
      this.hotjar = Hotjar;
      Hotjar.init(environment.hotjarSiteId, 6);
    });
  }

  public setChartMetaData(ver: SiteChartVersion): void {
    if (!environment.hotjarEnabled) return;

    void this.isReady().then(() => {
      const userId = localStorage.getItem('cognitoUserId') ?? null;
      this.hotjar!.identify(userId, {
        site_chart_version: ver === 'new' ? 'am5' : 'am4',
        site_chart_version_label: ver === 'new' ? 'New Chart' : 'Legacy Chart'
      });
    });
  }

  public trackSiteChartVersionSwitch(
    version: SiteChartVersion,
    trigger: SiteChartVersionTrigger
  ): void {
    this.setChartMetaData(version);

    if (!environment.hotjarEnabled || trigger === 'initial') {
      return;
    }

    void this.isReady().then(() =>
      this.hotjar?.event(this.getSiteChartVersionEventName(version, trigger))
    );
  }
}
